
import Item from "../menu-user/Item.vue";
import {
  computed,
  ComputedRef,
  defineComponent,
  onBeforeMount, ref
} from "vue";
import { useStore } from "vuex";

import { User } from "../../../../../../domain/entities/entities";
import { RepositoryException } from "../../../../../../domain/exceptions/exceptions";
import {
  dispatchErrorBox,
  isCirculoTemplate,
  isDecolaTemplate,
  isGTopTemplate
} from "../../../mixins";
import router from "@/routes";
import MenuItem, { ItemRoute } from "./MenuItem";
import { ExternalRoute, InternalRoute } from "../menu/MenuItem";
import { currentDomain } from "../../../../../repository/http";
import Suggestion from "../../../modals/Suggestion.vue";

export default defineComponent({
  name: "MenuUser",
  components: { Suggestion, Item },
  props: {
    isOpened: {
      type: Boolean,
      required: false
    }
  },

  setup() {
    const store = useStore();
    const showConfirmation = ref(false);

    const closeSuggestionModal = () => {
      showConfirmation.value = false;
    };

    const openSuggestion = () => {
      showConfirmation.value = true;
    };

    /** User getter.
     * @see User. */
    const user: ComputedRef<User> = computed(() => {
      return store.getters["userProfile_module/user"];
    });

    const isMaintenance = computed(
      () => store.getters["config_module/isMaintenance"]
    );

    const student = new MenuItem(
      "Meu perfil",
      "user.svg",
      new ItemRoute("profile", { option: "statistics" })
    );

    const events = new MenuItem(
      "Meus treinamentos",
      "events.svg",
      new ItemRoute("profile", { option: "courses" })
    );

    const adminPanel = new MenuItem(
      "Painel Admin.",
      "admin-icon.svg",
      new ExternalRoute(currentDomain + "/admin#/dashboard", "_blank")
    );
    adminPanel.isAlwaysEnabled = true;

    const managerPanel = new MenuItem(
      "Meus times",
      "admin-icon.svg",
      new ExternalRoute(currentDomain + "/admin#/team", "_blank")
    );
    managerPanel.isAlwaysEnabled = true;

    const statement = new MenuItem(
      "Extrato",
      "statement.svg",
      new ItemRoute("statement")
    );

    const order = new MenuItem(
      "Histórico de pedidos",
      "order.svg",
      new ItemRoute("profile", { option: "history-order" })
    );

    const indication = new MenuItem(
      "Treinamentos recomendados",
      "indication.svg",
      new ItemRoute("profile", { option: "indication" })
    );

    const favorite = new MenuItem(
      "Treinamentos favoritos",
      "heart.svg",
      new ItemRoute("profile", { option: "favorite" })
    );

    const dashboard = new MenuItem(
      "Início",
      "dashboard.svg",
      new ItemRoute("dashboard", { option: "dashboard" })
    );

    const userDashboard = new MenuItem(
      "Meu dashboard",
      "statistics.svg",
      new ItemRoute("statistics", { option: "chart" })
    );

    const shop = new MenuItem("Loja", "shop.svg", new ItemRoute("shop"));

    const suggestion = new MenuItem(
      "Dê sua sugestão",
      "suggestion.svg",
      null
    );
    suggestion.action = openSuggestion;


    /** Items that will be iterable and displayed on menu */
    const items: ComputedRef<MenuItem[]> = computed(() => {
      let menus: MenuItem[] = [
        dashboard,
        student,
        userDashboard,
        events,
        indication,
        favorite,
        suggestion
      ];

      if (isDecolaTemplate() || isGTopTemplate()) {
        menus.push(statement);
        menus.push(order);
        menus.push(shop);
      }
      return menus;
    });

    /** Tells whether the dropdown options list is open or not. */
    const isOpened: ComputedRef<boolean> = computed(() => {
      return store.getters["miscellany_module/headerDropdownIsOpen"];
    });

    onBeforeMount(() => {
      const user = store.getters["userProfile_module/user"] as User;
      switch (user.userType) {
        /** master and admin */
        case 2:
        case 5:
          items.value.unshift(adminPanel);
          break;
        /** manager */
        case 4:
          items.value.unshift(managerPanel);
          break;
      }
    });

    /** Logs the user out and directs them back to the landing page */
    async function logout(): Promise<void> {
      const wannaLeave = confirm("Deseja realmente sair?");

      if (!wannaLeave) {
        return;
      }

      try {
        await store.dispatch("authentication_module/logout");
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      } finally {
        await store.dispatch("miscellany_module/setDropdownMenuState", false);
        await store.dispatch("userProfile_module/updateUser", null);
        await router.push({ name: "home" });
      }
    }

    return {
      items,
      user,
      isOpened,
      showConfirmation,
      logout,
      closeSuggestionModal,
      openSuggestion,
      isMaintenance,
    };
  }
});
